import { message } from "antd";

export default class CustomUploadAdapter {
  loader: any;

  constructor(loader: any) {
    this.loader = loader;
  }

  // Sanitize filename
  sanitizeFileName(fileName: string): string {
    if (!fileName) return fileName;
    return fileName
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/[^\w.-]/g, '_')       // Replace special chars
      .toLowerCase();
  }

  upload() {
    return this.loader.file.then((file: File) => {

      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!validTypes.includes(file.type)) {
        //console.error(' Invalid file type:', file.type);
        return Promise.reject("Unsupported file type. Please upload a png or jpg file.");
      }

      // Validate file size (max 50MB)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        // console.error(' File too large:', file.size);
        return Promise.reject("File size should be less than 50MB.");
      }

      //  Initialize arrays if they don't exist (CRITICAL)
      if (!(window as any).pendingFaqImages) {
        //console.warn('Initializing pendingFaqImages in adapter');
        (window as any).pendingFaqImages = [];
      }
      if (!(window as any).blobUrlMappings) {
        //console.warn('Initializing blobUrlMappings in adapter');
        (window as any).blobUrlMappings = [];
      }
      if (!(window as any).serverImages) {
        //console.warn('Initializing serverImages in adapter');
        (window as any).serverImages = [];
      }

      //  Get current editor content to validate actual images present
      const currentEditorContent = (window as any).currentEditorContent || "";

      const pendingImages: File[] = (window as any).pendingFaqImages;
      const serverImages = (window as any).serverImages || [];

      //  Filter only images that are ACTUALLY in the editor
      const activeServerImages = serverImages.filter((img: any) =>
        currentEditorContent.includes(img.fileUrl)
      );

      //  VALIDATION 1: Maximum 20 images (server + pending + current)
      const totalImageCount = activeServerImages.length + pendingImages.length;
      if (totalImageCount >= 20) {
        return Promise.reject("Maximum 20 images allowed.");
      }

      const activePendingImages = pendingImages.filter((file: File, index: number) => {
        const mapping = (window as any).blobUrlMappings[index];
        return mapping && currentEditorContent.includes(mapping.blobUrl);
      });

      //  VALIDATION 2: Total combined size must not exceed 50MB
      const serverSize = activeServerImages.reduce((acc: number, img: any) => acc + (img.fileSize || 0), 0);
      const pendingSize = activePendingImages.reduce((acc: number, f: File) => acc + f.size, 0);
      const totalSize = serverSize + pendingSize + file.size;
      const maxTotalSize = 50 * 1024 * 1024; // 50MB

      if (totalSize > maxTotalSize) {
        const currentSizeMB = ((serverSize + pendingSize) / (1024 * 1024)).toFixed(2);
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        return Promise.reject("Maximum allowed image size is 50 MB.");
      }

      //  Create blob URL
      // Sanitize the filename
      const sanitizedFileName = this.sanitizeFileName(file.name);
      const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });

      const blobUrl = URL.createObjectURL(sanitizedFile);

      //  Check if this file is already pending (avoid duplicates)
      const existingIndex = (window as any).pendingFaqImages.findIndex(
        (f: File) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
      );

      if (existingIndex === -1) {
        //  New file - add to pending
        (window as any).pendingFaqImages.push(file);
        (window as any).blobUrlMappings.push({
          blobUrl: blobUrl,
          fileName: file.name,
          file: file,
          timestamp: Date.now() // Track when it was added
        });

      }

      // Return blob URL for CKEditor preview
      return { default: blobUrl };
    });
  }

  abort() {
    console.log('Upload aborted');
  }
}